// The load-bearing routing logic (Constraint 1, D1/D2) for the per-row
// "see replay" affordance: a pure function mapping a discovery row to the
// cross-surface audience-replay href — or `null` when no replay affordance
// applies. Kept free of React so the lifecycle matrix is unit-testable
// exhaustively, and free of `role`: replay visibility is enforced at the
// audience route / data layer by session privacy (ADR 0045, D3), not by the
// caller's role, so the helper takes only the row.
//
// Refinement: tasks/refinements/session_discovery/sd_see_replay_link.md
// TaskJuggler: session_discovery.sd_frontend.sd_see_replay_link
// ADR:        0026 (root router dispatches /a to SurfaceHost),
//             0043/0045 (audience replay surface + visibility gating).
//
// Status is recomputed from `startedAt`/`endedAt` via the shared
// `deriveLifecycleStatus` (D2) rather than threaded in, keeping the helper a
// self-contained function over the wire shape already on the row. The
// affordance is shown for every **started** row — live and ended alike — because
// a started session has a log to replay; only **lobby** rows (no log yet) get no
// replay link. A live row therefore shows BOTH "see replay" (the log-so-far) and
// "join live" (the running feed): this intentionally relaxes the session_discovery
// D6 "exactly one" invariant (sl_session_list_replay_always, D1):
//
//   lifecycle status | href
//   lobby            | — (null; no log to replay yet)
//   live             | /a/replay/:id (scrub the log-so-far; join-live coexists)
//   ended            | /a/replay/:id

import { deriveLifecycleStatus, type SessionListRow } from './SessionList';

/** The row fields the routing decision reads — id plus the lifecycle timestamps. */
export type SeeReplayRow = Pick<SessionListRow, 'id' | 'startedAt' | 'endedAt'>;

/**
 * Resolve the audience-replay destination for `row`, or `null` when the row is
 * a lobby session with no log yet (lobby rows have no see-replay affordance —
 * D2). Every started row (live or ended) resolves an href — keying off the one
 * no-log state rather than enumerating live/ended (D1). Locale is unprefixed;
 * the audience surface negotiates its own locale.
 */
export function seeReplayHref(row: SeeReplayRow): string | null {
  if (deriveLifecycleStatus(row) === 'lobby') {
    return null;
  }

  return `/a/replay/${row.id}`;
}
